import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useRef, useEffect } from 'react';
import AddPestMapping from '../components/AddPestMapping';
import DataTable from 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import $ from 'jquery';
import { getCropPestMappings, getCropPestMappingById } from '../lib/api';

const customStyles = `
  .dataTables_wrapper {
    padding: 0 !important;
    border: none !important;
    background: none !important;
  }
  .dataTables_wrapper .dataTables_filter,
  .dataTables_wrapper .dataTables_paginate,
  .dataTables_wrapper .dataTables_info,
  .dataTables_wrapper .dataTables_length {
    display: none !important;
  }
  .tooltip-menu {
    min-width: 140px;
    padding: 0.5rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    background: #fff;
    z-index: 1000;
  }
  .tooltip-item {
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: #4a4a4a;
  }
  .tooltip-item:hover {
    background-color: #f1f5f9;
    color: #1a73e8;
  }
    .card.h-100 {
    height: auto !important;
    min-height: 100%;
  }

  .table-responsive {
    overflow-y: visible !important;
    overflow-x: auto !important;
    max-height: none !important;
  }

  .card-body {
    height: auto !important;
    overflow: visible !important;
  }
`;

const PestMappingList = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [pestMappings, setPestMappings] = useState([]);
  const [pestMappingToEdit, setPestMappingToEdit] = useState(null); // will hold FULL payload from getCropPestMappingById
  const [showEntries, setShowEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const tableRef = useRef(null);
  const dataTableRef = useRef(null);
  const searchInputRef = useRef(null);

  const fetchPestMappings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await getCropPestMappings(token);

      if (response && response.data) {
        const transformedData = response.data.map((item, index) => ({
          id: item.CropPestID || item.CropID || index + 1,
          crop: item.CropName || 'N/A',
          pest: item.PestNames || 'No pests mapped',
          originalData: item
        }));
        setPestMappings(transformedData);
      } else {
        setPestMappings([]);
      }
    } catch (err) {
      console.error('Error fetching pest mappings:', err);
      setPestMappings([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleDrawer = (payload = null) => {
    setPestMappingToEdit(payload);
    setIsDrawerOpen((prev) => !prev);
  };

  const handleOpenForEdit = async (row) => {
    try {
      const token = localStorage.getItem('authToken');
      // Prefer CropPestID; fallback to row.id if needed
      const cropPestId =
        row?.originalData?.CropPestID ||
        row?.id;

      if (!cropPestId) {
        console.warn('No CropPestID found for edit');
        return;
      }

      const resp = await getCropPestMappingById(token, cropPestId);
      // Pass full payload as pestMappingToEdit (AddPestMapping will normalize robustly)
      const fullPayload = resp?.data || resp;
      toggleDrawer(fullPayload);
    } catch (e) {
      console.error('Failed to fetch mapping by id:', e);
    }
  };

  const handleSubmit = async () => {
    // Just refetch and close drawer on success
    await fetchPestMappings();
    setIsDrawerOpen(false);
    setPestMappingToEdit(null);
  };

  // Event delegation (DataTables path) – not used by our simple table
  useEffect(() => {
    const handleEditClick = (event) => {
      const editBtn = event.target.closest('.edit-btn');
      if (editBtn) {
        const mappingId = editBtn.getAttribute('data-mapping-id');
        const mapping = pestMappings.find(
          (m) => m?.originalData?.CropPestID === mappingId || String(m.id) === String(mappingId)
        );
        if (mapping) handleOpenForEdit(mapping);
      }
    };

    if (tableRef.current) {
      tableRef.current.addEventListener('click', handleEditClick);
    }
    return () => {
      if (tableRef.current) {
        tableRef.current.removeEventListener('click', handleEditClick);
      }
    };
  }, [pestMappings]);

  const initializeDataTable = () => {
    if (tableRef.current && !dataTableRef.current && pestMappings.length > 0) {
      dataTableRef.current = new DataTable(tableRef.current, {
        paging: true,
        pageLength: showEntries,
        lengthChange: false,
        searching: true,
        ordering: true,
        info: false,
        dom: 't',
        columnDefs: [
          {
            targets: 2,
            orderable: false,
            searchable: false,
            width: '120px',
            render: function (data, type, row, meta) {
              const originalData = pestMappings[meta.row];
              return `
                <div class="d-flex align-items-center gap-10 justify-content-center position-relative">
                  <button
                    type="button"
                    class="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                    data-mapping-id="${originalData?.originalData?.CropPestID || originalData?.id}"
                  >
                    <i class="menu-icon">✏️</i>
                  </button>
                </div>
              `;
            }
          }
        ],
        order: [[0, 'asc']],
        drawCallback: function () {
          if (dataTableRef.current) {
            const pageInfo = this.api().page.info();
            setCurrentPage(pageInfo.page + 1);
          }
        }
      });

      if (searchInputRef.current) {
        searchInputRef.current.addEventListener('input', (e) => {
          if (dataTableRef.current) {
            dataTableRef.current.search(e.target.value).draw();
          }
        });
      }
    }
  };

  const handlePreviousPage = () => {
    if (dataTableRef.current && currentPage > 1) {
      dataTableRef.current.page('previous').draw('page');
    } else {
      // Non-DataTables mode
      if (currentPage > 1) setCurrentPage((p) => p - 1);
    }
  };

  const handleNextPage = () => {
    if (dataTableRef.current) {
      const pageInfo = dataTableRef.current.page.info();
      if (currentPage < pageInfo.pages) {
        dataTableRef.current.page('next').draw('page');
      }
    } else {
      // Non-DataTables mode
      const totalPages = showEntries === -1 ? 1 : Math.ceil(pestMappings.length / showEntries);
      if (currentPage < totalPages) setCurrentPage((p) => p + 1);
    }
  };

  const handlePageChange = (page) => {
    if (dataTableRef.current) {
      dataTableRef.current.page(page - 1).draw('page');
    } else {
      setCurrentPage(page);
    }
  };

  // Ensure currentPage is valid when data, entries-per-page or mappings change
  useEffect(() => {
    const totalPages = showEntries === -1 ? 1 : Math.max(1, Math.ceil(pestMappings.length / showEntries));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
    // If there are no items, reset to page 1
    if (pestMappings.length === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [pestMappings, showEntries]);

  useEffect(() => {
    window.jQuery = window.$ = $;
    fetchPestMappings();
  }, []);

  useEffect(() => {
    if (pestMappings.length > 0 && !loading) {
      initializeDataTable();
    }
    return () => {
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }
    };
  }, [pestMappings, loading]);

  useEffect(() => {
    if (dataTableRef.current) {
      dataTableRef.current.page.len(showEntries).draw();
    }
  }, [showEntries]);

  // Simple (non-DataTables) table render with native edit buttons
  const renderTableWithoutDataTables = () => {
    const startIndex = (currentPage - 1) * showEntries;
    const endIndex =
      showEntries === -1
        ? pestMappings.length
        : Math.min(startIndex + showEntries, pestMappings.length);
    const currentData =
      showEntries === -1 ? pestMappings : pestMappings.slice(startIndex, endIndex);

    const totalPages =
      showEntries === -1 ? 1 : Math.ceil(pestMappings.length / showEntries);

    return (
      <>
        <div className="table-responsive">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th scope="col">Crop</th>
                <th scope="col">Pest</th>
                <th scope="col" className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((mapping) => (
                <tr key={mapping.id}>
                  <td>
                    <span className="text-md mb-0 fw-normal text-secondary-light">
                      {mapping.crop}
                    </span>
                  </td>
                  <td>
                    <span className="text-md mb-0 fw-normal text-secondary-light">
                      {mapping.pest}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="d-flex align-items-center gap-10 justify-content-center position-relative">
                      <button
                        type="button"
                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        onClick={() => handleOpenForEdit(mapping)}
                        title="Edit"
                      >
                        <Icon icon="lucide:edit" className="menu-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {currentData.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-3">No data</td>
                </tr>
              )}
            </tbody>
          </table>
</div>
        {showEntries !== -1 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {pestMappings.length === 0 ? 0 : startIndex + 1} to {endIndex} of {pestMappings.length} entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              <li className="page-item">
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>
              {[...Array(totalPages)].map((_, index) => (
                <li key={index} className="page-item">
                  <button
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                      currentPage === index + 1 ? 'bg-primary-600 text-white' : ''
                    }`}
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className="page-item">
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-nowrap gap-2">
            <span className="text-sm fw-medium text-secondary-light mb-0">Show</span>
            <select
              className="custom-select"
              value={showEntries}
              onChange={(e) => setShowEntries(parseInt(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={-1}>All</option>
            </select>

            <div className="navbar-search">
              <div className="relative">
                <input
                  type="text"
                  className="form-control h-[36px] min-w-[220px] pl-10 pr-3 text-sm border border-neutral-300 radius-6 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  name="search"
                  placeholder="Search crops or pests..."
                  ref={searchInputRef}
                  onChange={(e) => {
                    const term = e.target.value.toLowerCase();
                    const filtered = pestMappings.filter(
                      (m) =>
                        String(m.crop).toLowerCase().includes(term) ||
                        String(m.pest).toLowerCase().includes(term)
                    );
                  }}
                />
              </div>
            </div>
          </div>

          <button
            onClick={() => toggleDrawer(null)}
            className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add New Pest Mapping
          </button>
        </div>

        <div className="card h-100 p-16 radius-12">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            renderTableWithoutDataTables()
          )}
        </div>
      </div>

      <AddPestMapping
        isOpen={isDrawerOpen}
        onClose={() => {
          setPestMappingToEdit(null);
          setIsDrawerOpen(false);
        }}
        onSubmit={handleSubmit}
        pestMappingToEdit={pestMappingToEdit}
        onSuccess={fetchPestMappings}
      />
    </>
  );
};

export default PestMappingList;
